import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, Drawer, Fade, Paper } from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid } from '@mui/x-data-grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import './accounts.css';
import {
  useBulkDeleteAccountsMutation,
  useCreateAccountMutation,
  useGetAccountsQuery,
  useUpdateAccountMutation
} from 'services/accountApi';
import { useNotification } from 'hook/useNotification';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import AccountDetails from 'pages/accounts/components/AccountDetails';
import AccountCustomToolbar from 'pages/accounts/components/AccountCustomToolbar';

/**
 * Component for managing Accounts, including displaying a list of accounts, adding, updating,
 * and deleting accounts. Uses a DataGrid for listing the accounts and a Drawer for editing details.
 *
 * @component
 *
 * @example
 * <Accounts />
 *
 * @returns {React.Component} The Accounts management component.
 */
export default function Accounts() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const {
    data: accounts,
    error: accountsError,
    isLoading: accountsLoading
  } = useGetAccountsQuery();
  const [bulkDeleteAccount] = useBulkDeleteAccountsMutation();
  const [createAccount] = useCreateAccountMutation();
  const [updateAccount] = useUpdateAccountMutation();

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const columns = [
    {
      field: 'id',
      headerName: msg({ id: `page.table.column.id` }),
      width: 50,
      disableColumnMenu: true
    },
    { field: 'label', headerName: msg({ id: `page.table.column.label` }), width: 300 },
    {
      field: 'type',
      headerName: msg({ id: `page.table.column.type` }),
      width: 150
    },
    {
      field: 'host',
      headerName: msg({ id: `page.table.column.host` }),
      width: 150
    },
    {
      field: 'username',
      headerName: msg({ id: `page.table.column.username` }),
      width: 150
    }
  ];

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDetailDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleRowClick = (params) => {
    setSelectedAccount(params.row);
    setSelectedRowId(params.id);
    setDetailDrawerOpen(true);
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleRowSelectionModelChange = (rowSelectionModel) => {
    setRowSelectionModel(rowSelectionModel);
  };

  const handleAdd = () => {
    setSelectedAccount(null);
    setDetailDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (selectedRows) {
      bulkDeleteAccount(selectedRows.map((row) => row.id))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            title: msg({ id: 'notification.success.title' }),
            message: msg({ id: 'page.accounts.delete.success' })
          });
          clearAccountSelected();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.accounts.delete.error' })
          });
        });
    }
  };

  const handleFormSubmit = (data) => {
    if (!data.id) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDetailDrawerOpen(false);
  };

  const selectedRows = rowSelectionModel.map((id) => accounts.find((account) => account.id === id));

  if (accountsError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.accounts.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }} id="accountContainer">
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            rows={accounts}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
              selection: { ids: accounts && accounts.length > 0 ? [accounts[0].id] : [] },
              density: 'compact',
              sorting: {
                sortModel: [{ field: 'lastname', sort: 'asc' }]
              }
            }}
            slots={{ toolbar: AccountCustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: handleAdd,
                onDelete: handleDelete,
                selectedRows
              }
            }}
            loading={accountsLoading}
            pageSizeOptions={[20]}
            disableColumnResize
            checkboxSelection
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            getRowClassName={(params) => (params.id === selectedRowId ? 'Mui-selected' : '')}
          />
        </Paper>
        <Drawer
          variant="temporary"
          anchor="right"
          open={detailDrawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 700, p: 2 }
          }}
        >
          <AccountDetails
            account={selectedAccount}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
          />
        </Drawer>
        <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelClose} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleCloseDrawer} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </Fade>
  );

  function clearAccountSelected() {
    setSelectionModel([]);
    setRowSelectionModel([]);
    setSelectedAccount(null);
    setSelectedRowId(null);
  }

  function handleCreate(data) {
    createAccount(getAccountRequest(data))
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.accounts.create.success' })
        });
        clearAccountSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.accounts.create.error' })
        });
      });
  }

  function getAccountRequest(data) {
    return {
      ...data
    };
  }

  function handleUpdate(data) {
    updateAccount({
      accountId: data.id,
      accountRequest: getAccountRequest(data)
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.accounts.update.success' })
        });
        clearAccountSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.accounts.update.error' })
        });
      });
  }
}
